import { useState, useEffect, useRef, useCallback } from 'react';
import styles from './Notepad.module.css';
import { useWindowManager, WindowId } from '../Window/WindowManager';
import { useWindowResize } from '../Window/useWindowResize';
import { ResizeHandles } from '../Window/ResizeHandles';
import { WindowControls } from '../Window/WindowControls';
import { useFileSystem, HOME_DIR } from '../Window/FileSystem';
import { NotepadTextIcon } from '../icons';

interface NotepadProps {
  initialPath?: string;
  initialContent?: string;
  openKey?: number;
}

export function Notepad({ initialPath, initialContent, openKey }: NotepadProps) {
  const { writeFile, createFile } = useFileSystem();
  const [filePath, setFilePath] = useState<string | null>(initialPath || null);
  const [content, setContent] = useState(initialContent || '');
  const [originalContent, setOriginalContent] = useState(initialContent || '');
  const [isModified, setIsModified] = useState(false);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileNameInputRef = useRef<HTMLInputElement>(null);

  // Window manager - start closed
  const {
    zIndex,
    focus,
    close,
    minimize,
    maximize,
    open,
    isMinimized,
    isClosed,
    isMaximized,
  } = useWindowManager(WindowId.Notepad, 'Notepad', <NotepadTextIcon />, 'closed');

  // Window resize
  const { size, position, setPosition, isResizing, handleResizeStart } = useWindowResize(
    { width: 500, height: 400 },
    { x: 200, y: 100 },
    { minWidth: 300, minHeight: 200 }
  );

  // Dragging
  const [isDragging, setIsDragging] = useState(false);
  const dragOffset = useRef({ x: 0, y: 0 });
  const notepadRef = useRef<HTMLDivElement>(null);

  // Open file when openKey changes (handles reopening the same file)
  useEffect(() => {
    if (openKey === undefined || !initialPath || initialContent === undefined) return;
    setFilePath(initialPath);
    setContent(initialContent);
    setOriginalContent(initialContent);
    setIsModified(false);
    open();
  }, [openKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Track modifications
  useEffect(() => {
    setIsModified(content !== originalContent);
  }, [content, originalContent]);

  // Focus filename input when dialog opens
  useEffect(() => {
    if (showSaveDialog) {
      fileNameInputRef.current?.focus();
    }
  }, [showSaveDialog]);

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    if (!notepadRef.current) return;
    if (isMaximized) return;
    focus();
    const rect = notepadRef.current.getBoundingClientRect();
    dragOffset.current = {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    };
    setIsDragging(true);
  }, [focus, isMaximized]);

  useEffect(() => {
    if (!isDragging) return;

    const handlePointerMove = (e: PointerEvent) => {
      const desktopEl = document.querySelector('[data-desktop]') as HTMLElement;
      if (!desktopEl) return;

      const desktopRect = desktopEl.getBoundingClientRect();
      const notepadEl = notepadRef.current;
      if (!notepadEl) return;

      const notepadWidth = notepadEl.offsetWidth;
      const notepadHeight = notepadEl.offsetHeight;

      let newX = e.clientX - desktopRect.left - dragOffset.current.x;
      let newY = e.clientY - desktopRect.top - dragOffset.current.y;

      const dockHeight = 56;
      newX = Math.max(0, Math.min(newX, desktopRect.width - notepadWidth));
      newY = Math.max(0, Math.min(newY, desktopRect.height - notepadHeight - dockHeight));

      setPosition({ x: newX, y: newY });
    };

    const handlePointerUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
    window.addEventListener('pointercancel', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
      window.removeEventListener('pointercancel', handlePointerUp);
    };
  }, [isDragging, setPosition]);

  // Save existing file
  const handleSave = useCallback(() => {
    if (filePath) {
      const success = writeFile(filePath, content);
      if (success) {
        setOriginalContent(content);
        setIsModified(false);
      }
    } else {
      // No file path - show save dialog
      setShowSaveDialog(true);
      setSaveError(null);
      setNewFileName('');
    }
  }, [filePath, content, writeFile]);

  // Save new file
  const handleSaveNewFile = useCallback(() => {
    const trimmedName = newFileName.trim();

    if (!trimmedName) {
      setSaveError('Please enter a file name');
      return;
    }

    // Basic validation - no slashes or special chars
    if (/[/\\:*?"<>|]/.test(trimmedName)) {
      setSaveError('Invalid characters in file name');
      return;
    }

    if (trimmedName.endsWith('.png')) {
      setSaveError('Cannot save text files as .png');
      return;
    }

    const success = createFile(HOME_DIR, trimmedName, content);

    if (success) {
      const newPath = `${HOME_DIR}/${trimmedName}`;
      setFilePath(newPath);
      setOriginalContent(content);
      setIsModified(false);
      setShowSaveDialog(false);
      setNewFileName('');
      setSaveError(null);
    } else {
      setSaveError('File already exists or cannot be created');
    }
  }, [newFileName, content, createFile]);

  // Handle save dialog key events
  const handleSaveDialogKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSaveNewFile();
    } else if (e.key === 'Escape') {
      setShowSaveDialog(false);
      setSaveError(null);
    }
  }, [handleSaveNewFile]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSave]);

  // Focus textarea when window opens
  useEffect(() => {
    if (!isMinimized && !isClosed && !showSaveDialog) {
      textareaRef.current?.focus();
    }
  }, [isMinimized, isClosed, showSaveDialog]);

  // Close animation
  const [isClosing, setIsClosing] = useState(false);

  // Button handlers
  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsClosing(true);
  };

  const handleAnimationEnd = useCallback((e: React.AnimationEvent) => {
    if (e.animationName === styles['windowClose']) {
      setIsClosing(false);
      close();
    }
  }, [close]);

  const handleMinimize = (e: React.MouseEvent) => {
    e.stopPropagation();
    minimize();
  };

  const handleMaximize = (e: React.MouseEvent) => {
    e.stopPropagation();
    maximize();
  };

  // New file handler
  const handleNewFile = useCallback(() => {
    setFilePath(null);
    setContent('');
    setOriginalContent('');
    setIsModified(false);
  }, []);

  // Get display filename
  const getFileName = (): string => {
    if (!filePath) return 'Untitled';
    const parts = filePath.split('/');
    return parts[parts.length - 1];
  };

  // Get line and column info
  const getLineInfo = (): string => {
    const textarea = textareaRef.current;
    if (!textarea) return 'Ln 1, Col 1';

    const text = textarea.value.substring(0, textarea.selectionStart);
    const lines = text.split('\n');
    const line = lines.length;
    const col = lines[lines.length - 1].length + 1;

    return `Ln ${line}, Col ${col}`;
  };

  const [lineInfo, setLineInfo] = useState('Ln 1, Col 1');

  const handleSelectionChange = useCallback(() => {
    setLineInfo(getLineInfo());
  }, []);

  if (isClosed || isMinimized) {
    return null;
  }

  const windowStyle = isMaximized
    ? {
        left: 0,
        top: 0,
        width: '100%',
        height: 'calc(100% - 56px)',
        zIndex,
      }
    : {
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        zIndex,
      };

  return (
    <div
      ref={notepadRef}
      className={`${styles.notepad} ${isDragging || isResizing ? styles.dragging : ''} ${isMaximized ? styles.maximized : ''} ${isClosing ? styles.closing : ''}`}
      style={windowStyle}
      onPointerDown={focus}
      onAnimationEnd={handleAnimationEnd}
    >
      {!isMaximized && <ResizeHandles onResizeStart={handleResizeStart} />}

      {/* Title bar */}
      <div className={styles.notepadHeader} onPointerDown={handlePointerDown}>
        <WindowControls
          onClose={handleClose}
          onMinimize={handleMinimize}
          onMaximize={handleMaximize}
        />
        <span className={styles.notepadTitle}>
          {getFileName()}{isModified ? ' - (Unsaved changes)' : ''}
        </span>
      </div>

      {/* Menu bar */}
      <div className={styles.notepadMenuBar}>
        <button
          className={styles.notepadMenuButton}
          onClick={handleNewFile}
        >
          New
        </button>
        <button
          className={styles.notepadMenuButton}
          onClick={handleSave}
          disabled={!isModified && filePath !== null}
        >
          {filePath ? 'Save' : 'Save As'}
        </button>
      </div>

      {/* Save dialog overlay */}
      {showSaveDialog && (
        <div className={styles.notepadSaveDialog}>
          <div className={styles.saveDialogContent}>
            <div className={styles.saveDialogTitle}>Save File</div>
            <div className={styles.saveDialogField}>
              <label className={styles.saveDialogLabel}>File name:</label>
              <input
                ref={fileNameInputRef}
                type="text"
                className={styles.saveDialogInput}
                value={newFileName}
                onChange={(e) => setNewFileName(e.target.value)}
                onKeyDown={handleSaveDialogKeyDown}
                placeholder="document.txt"
                autoComplete="off"
              />
            </div>
            <div className={styles.saveDialogLocation}>
              Saving to: ~/
            </div>
            {saveError && (
              <div className={styles.saveDialogError}>{saveError}</div>
            )}
            <div className={styles.saveDialogButtons}>
              <button
                className={styles.saveDialogButton}
                onClick={() => {
                  setShowSaveDialog(false);
                  setSaveError(null);
                }}
              >
                Cancel
              </button>
              <button
                className={`${styles.saveDialogButton} ${styles.primary}`}
                onClick={handleSaveNewFile}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Editor */}
      <div className={styles.notepadBody}>
        <textarea
          ref={textareaRef}
          className={styles.notepadTextarea}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          onSelect={handleSelectionChange}
          onClick={handleSelectionChange}
          onKeyUp={handleSelectionChange}
          spellCheck={false}
          placeholder="Start typing..."
        />
      </div>

      {/* Status bar */}
      <div className={styles.notepadStatusBar}>
        <span>{lineInfo}</span>
        <span>{content.length} characters</span>
      </div>
    </div>
  );
}
